import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Bell, AlertTriangle, Send, Activity } from "lucide-react";
import { formatTimestamp } from "../lib/time";
import { T } from "../lib/i18n";

/** SuperAdmin / Coordinator alert console — in-app + optional Web Push. */
export default function AdminAlerts() {
  const [health, setHealth] = useState(null);
  const [history, setHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState("all");
  const [level, setLevel] = useState("normal");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [h, hi] = await Promise.allSettled([
        api.get("/push/health"), api.get("/admin/alerts/history"),
      ]);
      if (h.status === "fulfilled") setHealth(h.value.data);
      if (hi.status === "fulfilled") setHistory(hi.value.data);
    } catch (e) {}
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const send = async (emergency = false) => {
    if (!message.trim()) return toast.error("Enter a message");
    if (emergency && !window.confirm("Send EMERGENCY alert to all guests?")) return;
    setBusy(true);
    try {
      const path = emergency ? "/admin/alerts/emergency" : "/admin/alerts/broadcast";
      const { data } = await api.post(path, { message: message.trim(), target, level: emergency ? "emergency" : level, url });
      const push = data.push || {};
      toast.success(`Alert sent · in-app ✓ · push ${push.success ?? 0}/${push.attempted ?? 0}`);
      setMessage(""); setUrl("");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const testPush = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/push/test", { title: "Venuefy test", body: "This is a test alert" });
      if (data.ok === false) toast(`Push unavailable: ${data.reason || "unknown"} — in-app alerts still active`);
      else toast.success(`Test push: ${data.success}/${data.attempted}`);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const cfg = health?.config || {};
  return (
    <div className="space-y-4" data-testid="admin-alerts">
      <div className="card-elev p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-primary-motif" />
          <div className="font-display text-lg">{T("Push health")}</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
          <Kv label="VAPID configured" value={cfg.configured ? "Yes" : "No"} testid="push-vapid-status" />
          <Kv label="State" value={cfg.state || "—"} />
          <Kv label="Active subs" value={health?.active_subscriptions ?? 0} testid="push-active-subs" />
          <Kv label="Success / attempts" value={`${health?.total_success ?? 0} / ${health?.total_attempted ?? 0}`} />
        </div>
        {!cfg.configured && (
          <div className="mt-2 text-[10px] text-motif-muted italic" data-testid="push-not-configured">
            Background push is not configured. In-app alerts remain active.
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <button onClick={testPush} disabled={busy || !cfg.configured} className="btn-ghost text-xs" data-testid="push-test-me">{T("Send test alert to me")}</button>
        </div>
      </div>

      <div className="card-elev p-4">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-4 h-4 text-primary-motif" />
          <div className="font-display text-lg">{T("Send alert")}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <select className="input-motif" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="alert-target">
            <option value="all">{T("All guests")}</option>
            <option value="vip">{T("VIP guests")}</option>
            <option value="role">{T("Staff role")}</option>
          </select>
          <select className="input-motif" value={level} onChange={(e) => setLevel(e.target.value)} data-testid="alert-level">
            <option value="normal">{T("Normal")}</option>
            <option value="important">Important</option>
            <option value="emergency">{T("Emergency")}</option>
          </select>
          <input className="input-motif" placeholder="Deep link (optional)" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="alert-url" />
        </div>
        <textarea className="input-motif" rows={3} placeholder="Message…" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="alert-message" />
        <div className="flex gap-2 mt-2">
          <button onClick={() => send(false)} disabled={busy} className="btn-primary text-sm flex items-center gap-1" data-testid="alert-send">
            <Send className="w-3.5 h-3.5" /> Send alert
          </button>
          <button onClick={() => send(true)} disabled={busy} className="btn-ghost text-sm flex items-center gap-1" style={{ color: "#ef4444" }} data-testid="alert-emergency">
            <AlertTriangle className="w-3.5 h-3.5" /> Send Emergency Alert
          </button>
        </div>
      </div>

      <div className="card-elev p-4">
        <div className="text-[10px] tracking-luxe text-motif-muted mb-2">Recent alerts ({history.length})</div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {history.length === 0 && <div className="text-[11px] text-motif-muted italic">{T("No alerts sent yet")}</div>}
          {history.map((a) => (
            <div key={a.id} className="p-2 rounded-lg bg-accent-motif text-[11px]" data-testid={`alert-history-${a.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-bold" style={{ color: a.level === "emergency" ? "#ef4444" : "var(--motif-primary)" }}>{a.level.toUpperCase()}</span>
                <span className="text-motif">{a.message}</span>
              </div>
              <div className="text-[10px] text-motif-muted">{a.target} · {a.actor || "—"} · {formatTimestamp(a.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Kv({ label, value, testid }) {
  return (
    <div className="p-2 rounded-lg bg-accent-motif" data-testid={testid}>
      <div className="text-[9px] tracking-luxe text-motif-muted">{label}</div>
      <div className="text-motif font-medium">{value}</div>
    </div>
  );
}
